import { Box, Button, Collapse, InputAdornment, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTruckContextEnabled } from '../../../../constants/helpers/truck';
import { useCreateDailyTruckPlannedMutation, useUpdateDailyTruckPlannedMutation } from '../../../../hooks/mutations/useDailyTrucksPlannedMutation';
import { useDumpsQuery } from '../../../../hooks/queries/useDumpsQuery';
import { useLoadNaturesQuery } from '../../../../hooks/queries/useLoadNaturesQuery';
import { useQuarriesQuery } from '../../../../hooks/queries/useQuarriesQuery';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useTrucksCompaniesQuery } from '../../../../hooks/queries/useTrucksCompaniesQuery';
import { useTruckByCompanyQuery } from '../../../../hooks/queries/useTrucksQuery';
import { useTrucksTypesQuery } from '../../../../hooks/queries/useTrucksTypesQuery';
import { useBoolean } from '../../../../hooks/useBoolean';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { DailyTransfer, deleteDailyTransfer } from '../../../../services/apiSLTP/dailyReports/dailyTransfers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getMaterialStateColor } from '../../../../utils/colors';
import { getTruckModeInfos, materialState, stepModes, truckGroupEnum, truckMode } from '../../../../utils/enums';
import CustomTextInput from '../../../CustomTextInput';
import DropDown from '../../../DropDown';
import Icon from '../../../shared/Icon';
import IconButton from '../../../shared/IconButton';
import ModalBody, { ModalProps } from '../../ModalBody';
import ManageDailyTransferModal from './ManageDailyTransferModal';

interface ManageDailyTruckPlannedModalProps extends ModalProps {
    activity: DataItem;
    refetch: () => void;
    truckPlannedId: string;
    truckType?: number;
    isTruckSelectable?: boolean;
    stepMode?: number;
}

const ManageDailyTruckPlannedModal: React.FC<ManageDailyTruckPlannedModalProps> = ({
    open,
    handleClose,
    activity,
    refetch,
    truckPlannedId,
    truckType,
    isTruckSelectable = true,
    stepMode = stepModes.PLANNING,
}) => {
    const { t } = useTranslation();
    const [currentTruckPlannedId, setCurrentTruckPlannedId] = useState(truckPlannedId);
    const transferModal = useBoolean(false);

    const [selectedTrucksAmount, setSelectedTrucksAmount] = useState('1');
    const [selectedInitialTrucksAmount, setSelectedInitialTrucksAmount] = useState('');
    const [selectedRank, setSelectedRank] = useState('1');
    const [selectedRidesAmount, setSelectedRidesAmount] = useState('0');
    const [selectedCompany, setSelectedCompany] = useState({ label: 'SLTP', value: '6688062ffd2728b140ab7f1b' });
    const [selectedType, setSelectedType] = useState({ label: '', value: '' });
    const [selectedTruck, setSelectedTruck] = useState({ label: '', value: '' });
    const [selectedMode, setSelectedMode] = useState({ label: '', value: '' });

    const [selectedDepartureLoadNature, setSelectedDepartureLoadNature] = useState({ label: '', value: '' });
    const [selectedArrivalLoadNature, setSelectedArrivalLoadNature] = useState({ label: '', value: '' });
    const [selectedDepartureDump, setSelectedDepartureDump] = useState({ label: '', value: '' });
    const [selectedArrivalDump, setSelectedArrivalDump] = useState({ label: '', value: '' });
    const [selectedOrigin, setSelectedOrigin] = useState({ label: '', value: '', group: '' });
    const [selectedDestination, setSelectedDestination] = useState({ label: '', value: '', group: '' });
    const [observations, setObservations] = useState('');
    const [selectedTransfer, setSelectedTransfer] = useState<DailyTransfer | null>(null);

    const {
        data: dataCompanies,
        isLoading: isLoadingCompanies,
        isError: isErrorCompanies,
        isSuccess: isSuccessCompanies,
        refetch: refetchCompanies,
    } = useTrucksCompaniesQuery({ queries: { active: true } });

    const {
        data: dataTrucksTypes,
        isLoading: isLoadingTrucksTypes,
        isError: isErrorTrucksTypes,
        isSuccess: isSuccessTrucksTypes,
        refetch: refetchTrucksTypes,
    } = useTrucksTypesQuery({ queries: { active: true } });

    const {
        data: dataTrucks,
        isLoading: isLoadingTrucks,
        isError: isErrorTrucks,
        isSuccess: isSuccessTrucks,
        refetch: refetchTrucks,
    } = useTruckByCompanyQuery({ truckCompanyId: selectedCompany.value });

    const {
        data: dataSites,
        isLoading: isLoadingSites,
        isError: isErrorSites,
        isSuccess: isSuccessSites,
        refetch: refetchSites,
    } = useSitesQuery({ queries: { active: true } });

    const {
        data: dataLoadNatures,
        isLoading: isLoadingLoadNatures,
        isError: isErrorLoadNatures,
        isSuccess: isSuccessLoadNatures,
        refetch: refetchLoadNatures,
    } = useLoadNaturesQuery({ queries: { active: true } });

    const {
        data: dataQuarries,
        isLoading: isLoadingQuarries,
        isError: isErrorQuarries,
        isSuccess: isSuccessQuarries,
        refetch: refetchQuarries,
    } = useQuarriesQuery({ queries: { active: true } });

    const {
        data: dataDumps,
        isLoading: isLoadingDumps,
        isError: isErrorDumps,
        isSuccess: isSuccessDumps,
        refetch: refetchDumps,
    } = useDumpsQuery({ queries: { active: true } });

    const {
        mutate: createTruckPlanned,
        isSuccess: isCreateTruckPlannedSuccess,
        isError: isCreateTruckPlannedError,
        isLoading: isCreateTruckPlannedLoading,
        error: createTruckPlannedError,
    } = useCreateDailyTruckPlannedMutation({
        onSuccess: (data, variables) => {
            setCurrentTruckPlannedId(data.data._id);
            refetch();
        },
        onError: (error) => {
            console.error('Error creating truck planned:', error.message);
        },
    });

    const {
        mutate: updateTruckPlanned,
        isSuccess: isUpdateTruckPlannedSuccess,
        isError: isUpdateTruckPlannedError,
        isLoading: isUpdateTruckPlannedLoading,
        error: updateTruckPlannedError,
    } = useUpdateDailyTruckPlannedMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating truck planned:', error.message);
        },
    });

    const postDailyTruckPlanned = async () => {
        createTruckPlanned({
            activityId: activity._id,
            trucksAmount: parseInt(selectedTrucksAmount),
            ridesAmount: parseInt(selectedRidesAmount),
            truckTypeId: selectedType.value,
            initialTrucksAmount: selectedInitialTrucksAmount.length ? parseInt(selectedInitialTrucksAmount) : undefined,
            departureLoadNatureId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedDepartureLoadNature.value || null,
            arrivalLoadNatureId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedArrivalLoadNature.value || null,
            departureQuarryId:
                parseInt(selectedMode.value) === truckMode.TRANSFER
                    ? null
                    : selectedOrigin.group === capitalizeFirstLetter(t('quarries'))
                      ? selectedOrigin.value || null
                      : null,
            arrivalQuarryId:
                parseInt(selectedMode.value) === truckMode.TRANSFER
                    ? null
                    : selectedDestination.group === capitalizeFirstLetter(t('quarries'))
                      ? selectedDestination.value || null
                      : null,
            mode: parseInt(selectedMode.value),
            departureSiteId:
                parseInt(selectedMode.value) === truckMode.TRANSFER
                    ? null
                    : selectedOrigin.group === capitalizeFirstLetter(t('sites'))
                      ? selectedOrigin.value || null
                      : null,
            arrivalSiteId:
                parseInt(selectedMode.value) === truckMode.TRANSFER
                    ? null
                    : selectedDestination.group === capitalizeFirstLetter(t('sites'))
                      ? selectedDestination.value || null
                      : null,
            truckCompanyId: selectedCompany.value,
            truckId: selectedTruck.value || null,
            departureDumpId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedDepartureDump.value || null,
            arrivalDumpId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedArrivalDump.value || null,
            observations: observations,
            rank: parseInt(selectedRank),
        });
    };

    const patchDailyTruckPlanned = async () => {
        const dailyTruckPlanned = activity.dailyTrucksPlanned.find((value) => value._id === truckPlannedId);
        updateTruckPlanned({
            DailyTruckPlannedId: currentTruckPlannedId,
            data: {
                trucksAmount: parseInt(selectedTrucksAmount),
                ridesAmount: parseInt(selectedRidesAmount),
                initialTrucksAmount: selectedInitialTrucksAmount.length ? parseInt(selectedInitialTrucksAmount) : null,
                truckTypeId: selectedType.value,
                departureLoadNatureId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedDepartureLoadNature.value || null,
                arrivalLoadNatureId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedArrivalLoadNature.value || null,
                departureQuarryId:
                    parseInt(selectedMode.value) === truckMode.TRANSFER
                        ? null
                        : selectedOrigin.group === capitalizeFirstLetter(t('quarries'))
                          ? selectedOrigin.value || null
                          : null,
                arrivalQuarryId:
                    parseInt(selectedMode.value) === truckMode.TRANSFER
                        ? null
                        : selectedDestination.group === capitalizeFirstLetter(t('quarries'))
                          ? selectedDestination.value || null
                          : null,
                mode: parseInt(selectedMode.value),
                departureSiteId:
                    parseInt(selectedMode.value) === truckMode.TRANSFER
                        ? null
                        : selectedOrigin.group === capitalizeFirstLetter(t('sites'))
                          ? selectedOrigin.value || null
                          : null,
                arrivalSiteId:
                    parseInt(selectedMode.value) === truckMode.TRANSFER
                        ? null
                        : selectedDestination.group === capitalizeFirstLetter(t('sites'))
                          ? selectedDestination.value || null
                          : null,
                truckCompanyId: selectedCompany.value,
                truckId: selectedTruck.value || null,
                departureDumpId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedDepartureDump.value || null,
                arrivalDumpId: parseInt(selectedMode.value) === truckMode.TRANSFER ? null : selectedArrivalDump.value || null,
                observations: observations,
                rank: parseInt(selectedRank),
            },
        });
    };

    const removeDailyTransfer = async (transferId: string) => {
        await deleteDailyTransfer(transferId);
        refetch();
    };

    useEffect(() => {
        const dailyTruckPlanned = activity.dailyTrucksPlanned.find((value) => value._id === truckPlannedId);
        if (dailyTruckPlanned) {
            setSelectedRank(dailyTruckPlanned?.rank?.toString() || '1');
            setObservations(dailyTruckPlanned.observations || '');
            setSelectedCompany({
                label: dailyTruckPlanned.truckCompanyId?.designation || 'SLTP',
                value: dailyTruckPlanned.truckCompanyId?._id || '6688062ffd2728b140ab7f1b',
            });
            setSelectedDepartureLoadNature({
                label: dailyTruckPlanned.departureLoadNatureId?.designation || '',
                value: dailyTruckPlanned.departureLoadNatureId?._id || '',
            });
            setSelectedArrivalLoadNature({
                label: dailyTruckPlanned.arrivalLoadNatureId?.designation || '',
                value: dailyTruckPlanned.arrivalLoadNatureId?._id || '',
            });
            setSelectedMode({
                label:
                    capitalizeFirstLetter(
                        t(getTruckModeInfos(dailyTruckPlanned.mode !== undefined && dailyTruckPlanned.mode !== null ? dailyTruckPlanned.mode : -1))
                    ) || '',
                value: dailyTruckPlanned.mode?.toString() || '',
            });
            setSelectedOrigin({
                label: dailyTruckPlanned.departureQuarryId?.designation || dailyTruckPlanned.departureSiteId?.name || '',
                value: dailyTruckPlanned.departureQuarryId?._id || dailyTruckPlanned.departureSiteId?._id || '',
                group: dailyTruckPlanned.departureQuarryId ? capitalizeFirstLetter(t('quarries')) : capitalizeFirstLetter(t('sites')),
            });
            setSelectedDestination({
                label: dailyTruckPlanned.arrivalQuarryId?.designation || dailyTruckPlanned.arrivalSiteId?.name || '',
                value: dailyTruckPlanned.arrivalQuarryId?._id || dailyTruckPlanned.arrivalSiteId?._id || '',
                group: dailyTruckPlanned.arrivalQuarryId ? capitalizeFirstLetter(t('quarries')) : capitalizeFirstLetter(t('sites')),
            });
            setSelectedDepartureDump({
                value: dailyTruckPlanned.departureDumpId?._id || '',
                label: dailyTruckPlanned.departureDumpId?.designation || '',
            });
            setSelectedArrivalDump({
                value: dailyTruckPlanned.arrivalDumpId?._id || '',
                label: dailyTruckPlanned.arrivalDumpId?.designation || '',
            });
            setSelectedRidesAmount(dailyTruckPlanned.ridesAmount?.toString() || '0');
            setSelectedTruck({ label: dailyTruckPlanned.truckId?.designation || '', value: dailyTruckPlanned.truckId?._id || '' });
            setSelectedTrucksAmount(dailyTruckPlanned.trucksAmount.toString());
            setSelectedInitialTrucksAmount(dailyTruckPlanned.initialTrucksAmount?.toString() || '');
            setSelectedType({ label: dailyTruckPlanned.truckTypeId?.designation || '', value: dailyTruckPlanned.truckTypeId?._id || '' });
        }
    }, []);

    useEffect(() => {
        const dailyTruckPlanned = activity.dailyTrucksPlanned.find((value) => value._id === truckPlannedId);

        if (!dailyTruckPlanned && dataTrucksTypes) {
            const defaultType = dataTrucksTypes
                .filter((value) => value.group === truckType)
                .reduce((prev, curr) => {
                    const prevOrder = prev.displayOrder ?? Infinity;
                    const currOrder = curr.displayOrder ?? Infinity;
                    return prevOrder < currOrder ? prev : curr;
                });

            if (defaultType) {
                setSelectedType({ label: defaultType.designation, value: defaultType._id });
            }
        }
    }, [dataTrucksTypes]);

    return (
        <ModalBody
            title={t('trucksRides')}
            subTitle={!!currentTruckPlannedId.length ? t('update') : t('add')}
            description={activity.siteId.name}
            okText={t('validate')}
            disabledOk={
                !selectedType.value.length ||
                !selectedMode.value.length ||
                !selectedCompany.value.length ||
                !selectedRank.length ||
                !selectedTrucksAmount.length ||
                !selectedRidesAmount.length
            }
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !!currentTruckPlannedId.length ? patchDailyTruckPlanned() : postDailyTruckPlanned();
            }}
            okCanClose={!(!currentTruckPlannedId.length && selectedMode.value === truckMode.TRANSFER.toString())}
        >
            <Stack spacing={2}>
                <Stack spacing={0.5}>
                    {stepMode === stepModes.PLANNING && activity.reportId.truckId?.truckTypeId.group === truckGroupEnum.AMPLIROLL && (
                        <CustomTextInput
                            label={capitalizeFirstLetter(t('order'))}
                            type="number"
                            required
                            value={selectedRank}
                            onChange={(text) => setSelectedRank(text)}
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconButton
                                        iconName="remove"
                                        disabled={parseInt(selectedRank) <= 1}
                                        onClick={() => setSelectedRank(Math.max(1, (parseInt(selectedRank) || 1) - 1).toString())}
                                    />
                                </InputAdornment>
                            }
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        iconName="add"
                                        disabled={parseInt(selectedRank) >= 30}
                                        onClick={() => setSelectedRank(Math.min(30, (parseInt(selectedRank) || 1) + 1).toString())}
                                    />
                                </InputAdornment>
                            }
                        />
                    )}
                    <CustomTextInput
                        label={capitalizeFirstLetter(t('trucksNumber'))}
                        type="number"
                        value={selectedTrucksAmount}
                        onChange={(text) => setSelectedTrucksAmount(text)}
                        required
                        startAdornment={
                            <InputAdornment position="start">
                                <IconButton
                                    iconName="remove"
                                    disabled={parseInt(selectedTrucksAmount) <= 1}
                                    onClick={() => setSelectedTrucksAmount(Math.max(1, (parseInt(selectedTrucksAmount) || 1) - 1).toString())}
                                />
                            </InputAdornment>
                        }
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    iconName="add"
                                    disabled={parseInt(selectedTrucksAmount) >= 30}
                                    onClick={() => setSelectedTrucksAmount(Math.min(30, (parseInt(selectedTrucksAmount) || 0) + 1).toString())}
                                />
                            </InputAdornment>
                        }
                    />
                    {activity.reportId.userId.isSpecial && (
                        <CustomTextInput
                            label={capitalizeFirstLetter(t('initialTotalTrucksNumber'))}
                            type="number"
                            value={selectedInitialTrucksAmount}
                            onChange={(text) => setSelectedInitialTrucksAmount(text)}
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconButton
                                        iconName="remove"
                                        disabled={parseInt(selectedInitialTrucksAmount) <= 1}
                                        onClick={() =>
                                            setSelectedInitialTrucksAmount(Math.max(1, (parseInt(selectedInitialTrucksAmount) || 1) - 1).toString())
                                        }
                                    />
                                </InputAdornment>
                            }
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        iconName="add"
                                        disabled={parseInt(selectedInitialTrucksAmount) >= 30}
                                        onClick={() =>
                                            setSelectedInitialTrucksAmount(Math.min(30, (parseInt(selectedInitialTrucksAmount) || 0) + 1).toString())
                                        }
                                    />
                                </InputAdornment>
                            }
                        />
                    )}
                    <CustomTextInput
                        label={capitalizeFirstLetter(t('rides'))}
                        type="text"
                        required
                        value={selectedRidesAmount === '0' ? '∅' : selectedRidesAmount.toString()}
                        onChange={(text) => {
                            if (text.length === 0) setSelectedRidesAmount(text);
                            const numeric = parseInt(text);
                            if (!isNaN(numeric)) setSelectedRidesAmount(text);
                        }}
                        startAdornment={
                            <InputAdornment position="start">
                                <IconButton
                                    iconName="remove"
                                    disabled={parseInt(selectedRidesAmount) <= 0}
                                    onClick={() => setSelectedRidesAmount(Math.max(0, (parseInt(selectedRidesAmount) || 1) - 1).toString())}
                                />
                            </InputAdornment>
                        }
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    iconName="add"
                                    disabled={parseInt(selectedRidesAmount) >= 30}
                                    onClick={() => setSelectedRidesAmount(Math.min(30, (parseInt(selectedRidesAmount) || 0) + 1).toString())}
                                />
                            </InputAdornment>
                        }
                    />
                </Stack>
                <Stack spacing={0.5}>
                    <DropDown
                        selectedItem={selectedCompany}
                        items={dataCompanies?.map((company) => ({ label: company.designation, value: company._id })) || []}
                        handleChange={(event, value) => setSelectedCompany({ value: value?.value || '', label: value?.label || '' })}
                        inputLabel={t('company')}
                        loading={isLoadingCompanies}
                        required
                    />
                    <DropDown
                        selectedItem={selectedType}
                        items={dataTrucksTypes?.map((type) => ({ label: type.designation, value: type._id })) || []}
                        handleChange={(event, value) => {
                            if (value?.value !== selectedType.value) setSelectedTruck({ value: '', label: '' });
                            setSelectedType({ value: value?.value || '', label: value?.label || '' });
                        }}
                        inputLabel={t('type')}
                        loading={isLoadingTrucksTypes}
                        required
                    />
                    {stepMode === stepModes.PLANNING && (
                        <Collapse in={selectedCompany.label === 'SLTP' && !!selectedType.value.length}>
                            <DropDown
                                selectedItem={selectedTruck}
                                items={
                                    dataTrucks
                                        ?.filter((truck) => {
                                            return truck.truckCompanyId.isOwnCompany && truck.truckTypeId._id === selectedType.value;
                                        })
                                        .map((truck) => ({ label: truck.designation, value: truck._id })) || []
                                }
                                handleChange={(event, value) => setSelectedTruck({ value: value?.value || '', label: value?.label || '' })}
                                inputLabel={t('truck')}
                                loading={isLoadingTrucks}
                                disableClearable={false}
                            />
                        </Collapse>
                    )}
                </Stack>
                <DropDown
                    selectedItem={selectedMode}
                    items={Object.values(truckMode).map((mode) => ({
                        label: capitalizeFirstLetter(t(getTruckModeInfos(mode))),
                        value: mode.toString(),
                    }))}
                    handleChange={(event, value) => setSelectedMode({ value: value?.value || '', label: value?.label || '' })}
                    inputLabel={t('mode')}
                    sortItems={false}
                    required
                />

                <Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'departure')} unmountOnExit>
                    <Stack spacing={0.5}>
                        <Collapse in={dataTrucksTypes?.find((truck) => truck._id === selectedType.value)?.isDumpAllowed}>
                            <DropDown
                                selectedItem={selectedDepartureDump}
                                items={dataDumps?.map((dump) => ({ label: dump.designation, value: dump._id })) || []}
                                handleChange={(event, value) => setSelectedDepartureDump({ value: value?.value || '', label: value?.label || '' })}
                                inputLabel={t('dump')}
                                disableClearable={false}
                            />
                        </Collapse>
                        <DropDown
                            selectedItem={selectedDepartureLoadNature}
                            items={dataLoadNatures?.map((nature) => ({ label: nature.designation, value: nature._id })) || []}
                            handleChange={(event, value) => setSelectedDepartureLoadNature({ value: value?.value || '', label: value?.label || '' })}
                            inputLabel={t('loadNature')}
                            disableClearable={false}
                        />
                        <DropDown
                            selectedItem={selectedOrigin}
                            items={
                                dataSites
                                    ?.map((site) => ({ label: site.name, value: site._id, group: capitalizeFirstLetter(t('sites')) }))
                                    .concat(
                                        dataQuarries?.map((quarry) => ({
                                            label: quarry.designation,
                                            value: quarry._id,
                                            group: capitalizeFirstLetter(t('quarries')),
                                        })) || []
                                    ) || []
                            }
                            handleChange={(event, value) =>
                                setSelectedOrigin({
                                    value: value?.value || '',
                                    label: value?.label || '',
                                    group: value?.group || '',
                                })
                            }
                            inputLabel={t('origin')}
                            grouped
                            disableClearable={false}
                        />
                    </Stack>
                </Collapse>

                <Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'arrival')} unmountOnExit>
                    <Stack spacing={0.5}>
                        <Collapse in={dataTrucksTypes?.find((truck) => truck._id === selectedType.value)?.isDumpAllowed}>
                            <DropDown
                                selectedItem={selectedArrivalDump}
                                items={dataDumps?.map((dump) => ({ label: dump.designation, value: dump._id })) || []}
                                handleChange={(event, value) => setSelectedArrivalDump({ value: value?.value || '', label: value?.label || '' })}
                                inputLabel={t('dump')}
                                disableClearable={false}
                            />
                        </Collapse>

                        <DropDown
                            selectedItem={selectedArrivalLoadNature}
                            items={dataLoadNatures?.map((nature) => ({ label: nature.designation, value: nature._id })) || []}
                            handleChange={(event, value) => setSelectedArrivalLoadNature({ value: value?.value || '', label: value?.label || '' })}
                            inputLabel={t('loadNature')}
                            disableClearable={false}
                        />
                        <DropDown
                            selectedItem={selectedDestination}
                            items={
                                dataSites
                                    ?.map((site) => ({ label: site.name, value: site._id, group: capitalizeFirstLetter(t('sites')) }))
                                    .concat(
                                        dataQuarries?.map((quarry) => ({
                                            label: quarry.designation,
                                            value: quarry._id,
                                            group: capitalizeFirstLetter(t('quarries')),
                                        })) || []
                                    ) || []
                            }
                            handleChange={(event, value) =>
                                setSelectedDestination({
                                    value: value?.value || '',
                                    label: value?.label || '',
                                    group: value?.group || '',
                                })
                            }
                            inputLabel={t('destination')}
                            grouped
                            disableClearable={false}
                        />
                    </Stack>
                </Collapse>

                <Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'transfer') && !!currentTruckPlannedId.length} unmountOnExit>
                    <Stack spacing={1} px={3}>
                        {activity.dailyTrucksPlanned
                            .find((value) => value._id === currentTruckPlannedId)
                            ?.transfers.map((transfer) => (
                                <Box
                                    key={transfer._id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        borderRadius: 1,
                                        backgroundColor: getMaterialStateColor(transfer.state),
                                        color: 'black',
                                        px: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{ flexGrow: 1, cursor: 'pointer' }}
                                        onDoubleClick={() => {
                                            transferModal.setTrue();
                                            setSelectedTransfer(transfer);
                                        }}
                                    >
                                        {(transfer.machineId?.designation ||
                                            transfer.toolId?.designation ||
                                            transfer.rentalName ||
                                            transfer.rentalCompanyId?.designation) && (
                                            <Box>
                                                {transfer.machineId?.designation ||
                                                    transfer.toolId?.designation ||
                                                    `${transfer.rentalName} ${transfer.rentalCompanyId?.designation || ''}`}
                                            </Box>
                                        )}

                                        <Box>
                                            <b>{transfer.state === materialState.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                                            {transfer.siteId?.name || transfer.quarryId?.designation}
                                        </Box>
                                    </Box>

                                    <IconButton iconName="close" onClick={() => removeDailyTransfer(transfer._id)} sx={{ color: 'black' }} />
                                </Box>
                            ))}
                        <Button
                            variant="outlined"
                            startIcon={<Icon name="add" />}
                            onClick={() => {
                                transferModal.setTrue();
                                setSelectedTransfer(null);
                            }}
                        >
                            {capitalizeFirstLetter(t('addTransfer'))}
                        </Button>
                    </Stack>
                </Collapse>

                {/*<Collapse in={isTruckContextEnabled(parseInt(selectedMode.value), 'transfer') && !!currentTruckPlannedId.length} unmountOnExit>
                    <div className="fx-c gap-5px w-100">
                        <div className="w-100  fx-r fx-items-c gap-10px">
                            {capitalizeFirstLetter(t('transfers'))}
                            <div className="h-1px bg-lightgray w-100" />
                            <IconButton
                                iconName="addCircle"
                                onClick={() => {
                                    setShowModal(true);
                                    setSelectedTransfer(null);
                                }}
                                title={capitalizeFirstLetter(t('add'))}
                            />
                        </div>
                        <div className="fx-r ">
                            <div className=" max-h-300px of-y-auto w-100">
                                {activity.dailyTrucksPlanned
                                    .find((value) => value._id === currentTruckPlannedId)
                                    ?.transfers.map((transfer, index) => (
                                        <div
                                            className="border-radius-10px min-h-25px fx-r pad-l-5px fx-items-fs fx-justify-sb"
                                            style={{ backgroundColor: getMaterialStateColor(transfer.state), marginTop: index === 0 ? 0 : 5 }}
                                            key={transfer._id}
                                        >
                                            <div
                                                onDoubleClick={() => {
                                                    setShowModal(true);
                                                    setSelectedTransfer(transfer);
                                                }}
                                                className="fx-c w-100 text-black"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {(transfer.machineId?.designation ||
                                                    transfer.toolId?.designation ||
                                                    transfer.rentalName ||
                                                    transfer.rentalCompanyId?.designation) && (
                                                    <div>
                                                        {transfer.machineId?.designation ||
                                                            transfer.toolId?.designation ||
                                                            `${transfer.rentalName} ${transfer.rentalCompanyId?.designation || ''}`}
                                                    </div>
                                                )}

                                                <div>
                                                    <b>{transfer.state === materialState.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                                                    {transfer.siteId?.name || transfer.quarryId?.designation}
                                                </div>
                                            </div>
                                            <IconButton iconName="close" onClick={() => removeDailyTransfer(transfer._id)} />
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </Collapse>*/}

                <CustomTextInput value={observations} onChange={(text) => setObservations(text)} label={t('observations')} multiline />
                {transferModal.value && (
                    <ManageDailyTransferModal
                        handleClose={() => transferModal.setFalse()}
                        open={transferModal.value}
                        refetch={refetch}
                        truckPlannedId={currentTruckPlannedId}
                        transfer={selectedTransfer}
                        siteName={activity.siteId.name}
                    />
                )}
            </Stack>
        </ModalBody>
    );
};

export default ManageDailyTruckPlannedModal;
